<template>
  <div>
    <div class="full-height">
      <v-row justify="center" style="height: 90%">
        <v-col cols="11">
          <v-card class="pa-6 my-4" width="100%" raised height="100%">
            <div class="d-flex align-center">
              <v-icon color="primary" @click="onClickPrevBtn()"
                >arrow_back</v-icon
              >
              <div class="pl-2 text-h5 primary--text font-weight-medium">
                Additional Details
              </div>
            </div>
            <v-form>
              <ValidationObserver ref="dynamicFieldValuesForm">
                <template v-if="selectedItem && selectedItem.length > 0">
                  <div
                    v-for="(section, sectionIndex) in selectedItem"
                    :key="section.Section_Id"
                  >
                    <div
                      v-if="
                        fieldsWithConditions(
                          section.fields,
                          section.Section_Name
                        ).length
                      "
                      class="d-flex align-center justify-space-between mt-4 mb-2"
                    >
                      <div class="d-flex align-center">
                        <v-progress-circular
                          model-value="100"
                          :size="22"
                          class="mr-2"
                        ></v-progress-circular>
                        <span
                          class="text-h6 text-grey-darken-1 font-weight-bold"
                          >{{ section.Section_Name }}</span
                        >
                      </div>
                      <!-- Add button for multi-instance sections -->
                      <v-btn
                        v-if="
                          section.Allow_Multiple_Entries &&
                          section.Allow_Multiple_Entries.toLowerCase() === 'yes'
                        "
                        @click="addInstance(sectionIndex)"
                        color="primary"
                        text
                        size="small"
                      >
                        <v-icon>add</v-icon>
                        <span class="ml-1">Add</span>
                      </v-btn>
                    </div>
                    <!-- Render each instance for this section -->
                    <div
                      v-for="(entry, entryIndex) in section.entries"
                      :key="`${section.Section_Id}_${entryIndex}`"
                      class="mb-2"
                    >
                      <!-- Instance header with remove button -->
                      <div
                        v-if="
                          section.Allow_Multiple_Entries &&
                          section.Allow_Multiple_Entries.toLowerCase() ===
                            'yes' &&
                          section.entries.length > 1
                        "
                        class="d-flex align-center justify-end mb-2"
                      >
                        <v-btn
                          @click="removeInstance(sectionIndex, entryIndex)"
                          color="error"
                          text
                          size="small"
                        >
                          <v-icon size="16">fas fa-trash</v-icon>
                        </v-btn>
                      </div>
                      <v-row class="ma-4">
                        <v-col
                          v-for="field in fieldsWithConditions(
                            entry.fields,
                            entry.Section_Name
                          )"
                          :key="field.Custom_Field_Id"
                          cols="12"
                          md="4"
                          sm="6"
                        >
                          <!-- Single Choice / Multiple Choice -->
                          <ValidationProvider
                            v-if="
                              field.Custom_Field_Type.toLowerCase() ===
                                'single choice' ||
                              field.Custom_Field_Type.toLowerCase() ===
                                'multiple choice'
                            "
                            :ref="`Custom Select` + field.Custom_Field_Id"
                            v-slot="{ errors }"
                            :name="field.Custom_Field_Name"
                            :rules="field.Mandatory === 'Yes' ? 'required' : ''"
                          >
                            <v-autocomplete
                              v-model="field.Value"
                              :ref="`Custom Select` + field.Custom_Field_Id"
                              outlined
                              :items="parseDropdownItems(field.Dropdown_Values)"
                              :multiple="
                                field.Custom_Field_Type.toLowerCase() ===
                                'multiple choice'
                              "
                              clearable
                              @input="
                                onChangeFields(
                                  $event,
                                  field,
                                  sectionIndex,
                                  entryIndex
                                )
                              "
                              :error-messages="errors"
                            >
                              <template v-slot:label>
                                {{ field.Custom_Field_Name }}
                                <span
                                  v-if="field.Mandatory === 'Yes'"
                                  style="color: red"
                                  >*</span
                                ></template
                              >
                            </v-autocomplete>
                          </ValidationProvider>

                          <!-- URL Link -->
                          <a
                            v-else-if="
                              field.Custom_Field_Type.toLowerCase() === 'url' &&
                              field?.Url_Link
                            "
                            :href="field?.Url_Link"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-primary text-subtitle-1 font-weight-regular text-decoration-underline text-wrap text-left text-break d-inline-block"
                          >
                            {{
                              field.Custom_Field_Name
                                ? field.Custom_Field_Name
                                : ""
                            }}
                          </a>
                          <!-- Number Field -->
                          <ValidationProvider
                            v-else-if="
                              field.Custom_Field_Type.toLowerCase() === 'number'
                            "
                            :ref="`${field.Custom_Field_Id}`"
                            v-slot="{ errors }"
                            :name="field.Custom_Field_Name"
                            :rules="
                              field.Mandatory.toLowerCase() === 'yes'
                                ? `required|numeric|minimum:${field.Min_Validation}|maximum:${field.Max_Validation}`
                                : `numeric|minimum:${field.Min_Validation}|maximum:${field.Max_Validation}`
                            "
                          >
                            <v-text-field
                              v-model="field.Value"
                              outlined
                              type="number"
                              clearable
                              :error-messages="errors"
                              @input="
                                onChangeFields(
                                  $event,
                                  field,
                                  sectionIndex,
                                  entryIndex
                                )
                              "
                            >
                              <template v-slot:label>
                                {{ field.Custom_Field_Name }}
                                <span
                                  v-if="field.Mandatory.toLowerCase() === 'yes'"
                                  style="color: red"
                                  >*</span
                                ></template
                              >
                            </v-text-field>
                          </ValidationProvider>

                          <!-- Text Area -->
                          <ValidationProvider
                            v-else-if="
                              field.Custom_Field_Type.toLowerCase() ===
                              'text area'
                            "
                            :ref="`${field.Custom_Field_Id}`"
                            v-slot="{ errors }"
                            :name="field.Custom_Field_Name"
                            :rules="
                              field.Mandatory.toLowerCase() === 'yes'
                                ? `required|min:${parseInt(
                                    field.Min_Validation
                                  )}|max:${parseInt(
                                    field.Max_Validation
                                  )}|regex:${field.Validation_Id}`
                                : `min:${parseInt(
                                    field.Min_Validation
                                  )}|max:${parseInt(
                                    field.Max_Validation
                                  )}|regex:${field.Validation_Id}`
                            "
                          >
                            <v-textarea
                              v-model="field.Value"
                              outlined
                              rows="1"
                              clearable
                              :counter="field.Max_Validation"
                              :maxlength="field.Max_Validation"
                              :error-messages="errors"
                              @input="
                                onChangeFields(
                                  $event,
                                  field,
                                  sectionIndex,
                                  entryIndex
                                )
                              "
                            >
                              <template v-slot:label>
                                {{ field.Custom_Field_Name }}
                                <span
                                  v-if="field.Mandatory.toLowerCase() === 'yes'"
                                  style="color: red"
                                  >*</span
                                ></template
                              >
                            </v-textarea>
                          </ValidationProvider>

                          <!-- Date -->
                          <ValidationProvider
                            v-else-if="
                              field.Custom_Field_Type.toLowerCase() === 'date'
                            "
                            :ref="`date` + field.Custom_Field_Id"
                            v-slot="{ errors }"
                            :name="field.Custom_Field_Name"
                            :rules="
                              field.Mandatory.toLowerCase() === 'yes'
                                ? 'required'
                                : ''
                            "
                          >
                            <v-text-field
                              v-model="field.Value"
                              type="date"
                              outlined
                              clearable
                              :error-messages="errors"
                              @input="
                                onChangeFields(
                                  $event,
                                  field,
                                  sectionIndex,
                                  entryIndex
                                )
                              "
                            >
                              <template v-slot:label>
                                {{ field.Custom_Field_Name }}
                                <span
                                  v-if="field.Mandatory.toLowerCase() === 'yes'"
                                  style="color: red"
                                  >*</span
                                >
                              </template>
                            </v-text-field>
                          </ValidationProvider>

                          <!-- Text Field -->
                          <ValidationProvider
                            v-else-if="
                              field.Custom_Field_Type.toLowerCase() ===
                              'text field'
                            "
                            :ref="`${field.Custom_Field_Id}`"
                            v-slot="{ errors }"
                            :name="field.Custom_Field_Name"
                            :rules="
                              field.Mandatory.toLowerCase() === 'yes'
                                ? `required|min:${parseInt(
                                    field.Min_Validation
                                  )}|max:${parseInt(
                                    field.Max_Validation
                                  )}|regex:${field.Validation_Id}`
                                : `min:${parseInt(
                                    field.Min_Validation
                                  )}|max:${parseInt(
                                    field.Max_Validation
                                  )}|regex:${field.Validation_Id}`
                            "
                          >
                            <v-text-field
                              v-model="field.Value"
                              outlined
                              clearable
                              :counter="field.Max_Validation"
                              :maxlength="field.Max_Validation"
                              :error-messages="errors"
                              @input="
                                onChangeFields(
                                  $event,
                                  field,
                                  sectionIndex,
                                  entryIndex
                                )
                              "
                            >
                              <template v-slot:label>
                                {{ field.Custom_Field_Name }}
                                <span
                                  v-if="
                                    field.Mandatory?.toLowerCase() === 'yes'
                                  "
                                  style="color: red"
                                  >*</span
                                ></template
                              >
                            </v-text-field>
                          </ValidationProvider>

                          <DragAndDropFiles
                            v-if="
                              field.Custom_Field_Type.toLowerCase() ===
                              'document upload'
                            "
                            :id="`${field.Custom_Field_Id}`"
                            file-category="document"
                            :key-index="
                              'document-file-upload-' + field.Custom_Field_Id
                            "
                            :old-file-name="
                              field.Value && field.Value.formattedName
                                ? field.Value.formattedName
                                : ''
                            "
                            :is-error="field.fileValidationError"
                            :Mandatory="true"
                            :message="true"
                            @file-event-success="
                              (field.Value = { formattedName: $event }),
                                (field.fileValidationError = '')
                            "
                            @file-event-triggered="isFileLoading = $event"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </template>
              </ValidationObserver>
            </v-form>
            <v-divider></v-divider>
            <div class="mt-3 d-flex justify-end">
              <v-btn
                color="primary"
                style="border-radius: 10px"
                large
                :loading="saveLoading || listLoading"
                @click="validateAddEdit()"
                >Save</v-btn
              >
            </div>
            <v-overlay
              absolute
              :value="saveLoading || listLoading"
              color="#fff"
              z-index="1"
            >
              <v-progress-circular
                color="primary"
                indeterminate
                size="50"
              ></v-progress-circular>
            </v-overlay> </v-card
        ></v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { ADD_UPDATE_CUSTOM_FIELD_VALUES } from "@/graphql/OnboardingQueries";
import { scrollScreenToTop } from "../../../helper";
import moment from "moment";
import DragAndDropFiles from "@/components/custom-components/DragAndDropFiles.vue";

export default {
  name: "DynamicCustomFields",

  components: { DragAndDropFiles },

  data: () => ({
    isUpdate: false,
    isManualFundAddress: false,
    // custom fields
    saveLoading: false,
    isFormDirty: false,
    selectedItem: [],
  }),
  props: {
    listCustomFields: {
      type: Array,
      default: () => [],
    },
    listLoading: {
      type: Boolean,
      default: false,
    },
    candidateId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    formatDate() {
      return (date) => {
        return date && moment(date).isValid()
          ? moment(date).format("DD/MM/YYYY")
          : "";
      };
    },
    fieldsWithConditions() {
      return (fields) => {
        let originalList = fields;

        originalList = originalList.filter((field) => {
          if (field.Visibility_Condition) {
            const visibilityCondition =
              typeof field.Visibility_Condition === "string"
                ? JSON.parse(field.Visibility_Condition)
                : field.Visibility_Condition;
            // Get the parent field value
            const parentField = originalList.find(
              (item) =>
                item.Custom_Field_Id === visibilityCondition?.Custom_Field_Id
            );
            const fieldValue = parentField?.Value;
            // If Type is "Any", show the field if parent field has any value
            if (visibilityCondition.Type?.toLowerCase() !== "specific") {
              // For different field types, check if they have a value
              if (
                parentField?.Custom_Field_Type?.toLowerCase() ===
                "multiple choice"
              )
                return Array.isArray(fieldValue) && fieldValue.length > 0;
              else return !!fieldValue;
            }

            // Otherwise, check for specific value match (Type="Specific" or undefined for backward compatibility)

            if (field?.Custom_Field_Type?.toLowerCase() === "date") {
              const formattedFieldValue = this.formatDate(fieldValue);
              const formattedConditionValue = this.formatDate(
                visibilityCondition.Value
              );
              return formattedFieldValue === formattedConditionValue;
            }
            if (
              typeof visibilityCondition.Value === "string" &&
              visibilityCondition.Value.startsWith("[") &&
              visibilityCondition.Value.endsWith("]")
            ) {
              let valueArray = JSON.parse(visibilityCondition.Value);
              if (Array.isArray(fieldValue)) {
                return fieldValue.some((val) => valueArray.includes(val));
              } else {
                return valueArray.includes(fieldValue);
              }
            } else return fieldValue == visibilityCondition?.Value;
          }
          return true;
        });

        return originalList;
      };
    },
  },
  emits: ["back-to-prev", "updated-dynamic-form"],

  mounted() {
    scrollScreenToTop();
    this.selectedItem = this.listCustomFields;
    this.$emit("updated-dynamic-form");
    localStorage.setItem("currentScreen", "6");
  },

  methods: {
    onClickPrevBtn() {
      this.$emit("back-to-prev");
    },
    // Multi-instance methods
    addInstance(sectionIndex) {
      const section = this.selectedItem[sectionIndex];
      if (section && section.Allow_Multiple_Entries?.toLowerCase() === "yes") {
        // Create a new entry with default field values
        const newEntry = {
          Custom_Field_Value_Id: null,
          fields: section.entries[0].fields.map((field) => ({
            ...field,
            Value: this.getDefaultFieldValue(field),
            // Reset date-specific properties
            dateMenu:
              field.Custom_Field_Type?.toLowerCase() === "date"
                ? false
                : undefined,
            formattedDate:
              field.Custom_Field_Type?.toLowerCase() === "date"
                ? ""
                : undefined,
          })),
        };

        section.entries.splice(0, 0, newEntry);
        this.isFormDirty = true;
      }
    },
    removeInstance(sectionIndex, entryIndex) {
      const section = this.selectedItem[sectionIndex];
      if (section && section.entries.length > 1) {
        section.entries.splice(entryIndex, 1);
        this.isFormDirty = true;
      }
    },
    getDefaultFieldValue(field) {
      switch (field.Custom_Field_Type?.toLowerCase()) {
        case "multiple choice":
          return [];
        case "date":
          return null;
        case "document upload":
          return [];
        default:
          return null;
      }
    },
    onChangeFields(newValue, field, sectionIndex, entryIndex) {
      this.isFormDirty = true;

      // Update field value
      field.Value = newValue;

      // For multi-instance support, update the specific field in the specific entry
      if (sectionIndex !== undefined && entryIndex !== undefined) {
        const targetField = this.selectedItem[sectionIndex].entries[
          entryIndex
        ].fields.find((f) => f.Custom_Field_Id === field.Custom_Field_Id);
        if (targetField) {
          targetField.Value = newValue;
        }
      }

      // // Handle visibility conditions for multi-instance structure
      this.handleVisibilityConditions(
        field,
        newValue,
        sectionIndex,
        entryIndex
      );
    },

    handleVisibilityConditions(
      changedField,
      newValue,
      sectionIndex,
      entryIndex
    ) {
      // Get the changed field
      if (!changedField || !changedField.Custom_Field_Id) return;

      let childFields = [];

      // For multi-instance structure only
      if (
        sectionIndex !== undefined &&
        entryIndex !== undefined &&
        this.selectedItem[sectionIndex]
      ) {
        const currentEntry =
          this.selectedItem[sectionIndex].entries[entryIndex];
        if (currentEntry) {
          childFields = currentEntry.fields.filter((item) => {
            if (!item.Visibility_Condition) return false;

            const visibilityCondition =
              typeof item.Visibility_Condition === "string"
                ? JSON.parse(item.Visibility_Condition)
                : item.Visibility_Condition;

            return (
              visibilityCondition.Custom_Field_Id ===
              changedField.Custom_Field_Id
            );
          });
        }
      }

      // For each child field, check if the condition is still met
      childFields.forEach((childField) => {
        const visibilityCondition =
          typeof childField.Visibility_Condition === "string"
            ? JSON.parse(childField.Visibility_Condition)
            : childField.Visibility_Condition;

        let conditionMet = false;
        // If Type is "Any", check if the parent field has any value
        if (visibilityCondition.Type?.toLowerCase() === "any") {
          if (
            changedField.Custom_Field_Type?.toLowerCase() === "multiple choice"
          )
            conditionMet = Array.isArray(newValue) && newValue.length > 0;
          else conditionMet = !!newValue;
        }
        // Otherwise, check for specific value match (Type="Specific" or undefined for backward compatibility)
        else {
          // Check if the condition is still met based on field type
          if (changedField.Custom_Field_Type?.toLowerCase() === "date") {
            const formattedFieldValue = this.formatDate(newValue);
            const formattedConditionValue = this.formatDate(
              visibilityCondition.Value
            );
            conditionMet = formattedFieldValue === formattedConditionValue;
          } else if (
            typeof visibilityCondition.Value === "string" &&
            visibilityCondition.Value.startsWith("[") &&
            visibilityCondition.Value.endsWith("]")
          ) {
            let valueArray = JSON.parse(visibilityCondition.Value);
            if (Array.isArray(newValue))
              conditionMet = newValue.some((val) => valueArray.includes(val));
            else conditionMet = valueArray.includes(newValue);
          } else conditionMet = newValue == visibilityCondition.Value;
        }

        // If condition is no longer met, reset the child field's value
        if (!conditionMet && childField.Value) {
          // Reset based on field type
          if (childField.Custom_Field_Type?.toLowerCase() === "date") {
            childField.Value = null;
            childField.formattedDate = "";
          } else if (
            childField.Custom_Field_Type?.toLowerCase() === "multiple choice"
          ) {
            childField.Value = [];
          } else {
            childField.Value = null;
          }

          // For multi-instance structure, also update the corresponding field in selectedItem
          if (
            sectionIndex !== undefined &&
            entryIndex !== undefined &&
            this.selectedItem[sectionIndex]
          ) {
            const targetField = this.selectedItem[sectionIndex].entries[
              entryIndex
            ].fields.find(
              (f) => f.Custom_Field_Id === childField.Custom_Field_Id
            );
            if (targetField) {
              targetField.Value = childField.Value;
              if (childField.Custom_Field_Type?.toLowerCase() === "date") {
                targetField.formattedDate = "";
              }
            }
          }
        }
      });
    },
    parseDropdownItems(value) {
      return JSON.parse(value);
    },
    validateAddEdit() {
      let vm = this;
      vm.$refs.dynamicFieldValuesForm.validate().then((isValid) => {
        let invalidFields = [];
        Object.keys(this.$refs).forEach((refName) => {
          const field = this.$refs[refName];
          if (field && field.errors && field.errors.length > 0) {
            invalidFields.push(refName);
          }
        });
        if (invalidFields.length > 0) {
          let firstField = this.$refs[invalidFields[0]];
          firstField.$el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        if (isValid) {
          vm.saveLoading = true;
          vm.addUpdateFormStructure();
        }
      });
    },
    addUpdateFormStructure() {
      let fieldValues = [];
      if (this.selectedItem && this.selectedItem.length > 0) {
        this.selectedItem.forEach((section) => {
          section.entries.forEach((entry) => {
            let entryValues = {};
            entry.fields.forEach((field) => {
              let fieldValue = field.Value;
              if (field.Custom_Field_Type?.toLowerCase() === "date") {
                fieldValue = moment(fieldValue).isValid()
                  ? moment(fieldValue).format("YYYY-MM-DD")
                  : null;
              }
              const fieldKey = `${field.Custom_Field_Id}`;
              entryValues[fieldKey] = fieldValue;
            });
            fieldValues.push({
              Primary_Id: this.candidateId,
              Form_Id: 178,
              Section_Id: section.Section_Id,
              Custom_Field_Value_Id: entry.Custom_Field_Value_Id,
              Field_Value: JSON.stringify(entryValues),
            });
          });
        });
      }
      this.addUpdateCustomFields(fieldValues);
    },
    addUpdateCustomFields(fieldValues = []) {
      let vm = this;
      vm.$apollo
        .mutate({
          mutation: ADD_UPDATE_CUSTOM_FIELD_VALUES,
          variables: {
            Form_Id: 178,
            entries: fieldValues,
          },
          client: "apolloClientE",
        })
        .then(() => {
          vm.$emit("updated-dynamic-form");
          vm.$emit("additional-saved");
          vm.saveLoading = false;
        })
        .catch((addErr) => {
          let snackbarData = {
            isOpen: true,
            message:
              addErr.graphQLErrors.length !== 0
                ? addErr.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },
    showAlert(snackbarData) {
      this.$store.commit("OPEN_SNACKBAR", snackbarData);
    },
  },
};
</script>
